import { CSSObject } from '@emotion/react';
import { shapes } from './shapes';
import { colors } from './colors';
import { fonts } from './fonts';
import { breakpoints } from './breakpoints';

// Exported so controls outside `.field-grouping` (e.g. ButtonToggle) can line up with it.
export const LABEL_GAP = 12;

export const LABEL_SMALL = 107;
export const LABEL_LARGE = 148;

// The one dropdown look, shared by native <select>s (formCss below), MUI selects (theme.ts and
// commons/form/Select.tsx) and the plan editor's View filter: white, a grey border, the
// app's rounded corners, and a thin blue chevron on the right. Change it here, not per screen.
// The same border as MUI's outlined text fields (the search box), so a dropdown and the search
// beside it read as one pair.
export const DROPDOWN_BORDER_COLOR = 'rgba(0, 0, 0, 0.23)';
export const DROPDOWN_CHEVRON_COLOR = colors.blue3;

// A thin chevron (a 24px "v") as a CSS background, for native <select>s.
const chevronImage = (color: string) =>
  `url("data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">' +
      `<path d="M7 10l5 5 5-5" stroke="${color}" stroke-width="2" ` +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>',
  )}")`;

export const formCss: CSSObject = {
  hr: {
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: colors.dividerGrey,
    borderBottomWidth: 'thin',
    flexShrink: 0,
  },
  'input, textarea, select': {
    ...fonts.text,
    width: '100%',
    padding: '0 10px',
    borderRadius: shapes.borderRadius,
    border: `1px solid ${colors.dividerGrey}`,
    boxSizing: 'border-box',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    '&:hover, &:focus, &:active': {
      border: `1px solid ${colors.blue4}`,
    },
    '&:disabled': {
      border: `1px solid ${colors.dividerGrey}`,
      cursor: 'default',
      color: colors.dividerGrey,
    },
  },

  'input, select': {
    height: '40px',
  },
  textarea: {
    padding: '10px',
    resize: 'none',
  },

  'input[type="checkbox"]': {
    display: 'grid',
    verticalAlign: 'middle',
    appearance: 'none',
    margin: 0,
    font: 'inherit',
    color: 'currentcolor',
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    placeContent: 'center',
    backgroundColor: colors.white,
    '&::before': {
      content: '""',
      width: '20px',
      height: '20px',
      transform: 'scale(0)',
      transition: '120ms transform ease-in-out',
      boxShadow: `inset 20px 20px ${colors.white}`,
    },
  },

  'input[type="checkbox"]:checked': {
    backgroundColor: colors.blue3,
    borderColor: colors.blue3,
    '&::before': {
      transform: 'scale(1)',
      width: '12px',
      height: '9px',
      transformOrigin: 'bottom left',
      clipPath:
        // eslint-disable-next-line max-len
        'path("M4.07573 8.82324L0.175729 4.90081C-0.0585762 4.66516 -0.0585762 4.28308 0.175729 4.0474L1.02424 3.19399C1.25854 2.95832 1.63846 2.95832 1.87277 3.19399L4.5 5.8363L10.1272 0.176739C10.3615 -0.058913 10.7415 -0.058913 10.9758 0.176739L11.8243 1.03015C12.0586 1.2658 12.0586 1.64789 11.8243 1.88356L4.92426 8.82326C4.68994 9.05892 4.31004 9.05892 4.07573 8.82324Z")',
    },
    '&:disabled': {
      backgroundColor: colors.dividerGrey,
    },
  },

  '.checkbox-label': {
    ...fonts.text,
    display: 'grid',
    gridTemplateColumns: '20px auto',
    gap: '10px',
    lineHeight: '20px',
    [breakpoints.small]: {
      marginLeft: LABEL_SMALL + LABEL_GAP,
    },

    [breakpoints.large]: {
      marginLeft: LABEL_LARGE + LABEL_GAP,
    },
  },

  select: {
    margin: '0',
    boxSizing: 'border-box',
    paddingRight: '36px',
    appearance: 'none',
    backgroundColor: colors.white,
    borderColor: DROPDOWN_BORDER_COLOR,
    backgroundImage: chevronImage(DROPDOWN_CHEVRON_COLOR),
    backgroundSize: '22px',
    backgroundPosition: 'right 8px center',
    backgroundRepeat: 'no-repeat',
    '&:hover, &:focus, &:active': {
      borderColor: DROPDOWN_CHEVRON_COLOR,
    },
    '&:disabled': {
      borderColor: colors.dividerGrey,
      backgroundImage: chevronImage(colors.darkGrey),
    },
  },

  '.field-grouping': {
    display: 'flex',
    alignItems: 'center',
    gap: LABEL_GAP,
    width: 'fit-content',
    '@media (max-width: 600px)': {
      width: '100%',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    '&>label': {
      flexGrow: 0,
      flexShrink: 0,
      ...fonts.largeLabel,
      display: 'flex',

      [breakpoints.small]: {
        width: LABEL_SMALL,
      },

      [breakpoints.large]: {
        width: LABEL_LARGE,
      },

      '&.required': {
        '&::after': {
          content: '" *"',
          color: colors.red,
        },
      },
    },
    '&.error': {
      'input, select': {
        border: `1px solid ${colors.red}`,
      },
    },
  },

  '.checkbox-field-grouping': {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: LABEL_GAP,
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
    },
    '& > legend': {
      ...fonts.largeLabel,
    },
    '&.side-label': {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      '& > legend': {
        width: 88,
      },
    },
    '&.error': {
      input: {
        border: `1px solid ${colors.red}`,
      },
    },
  },

  '.old-form': {
    '.field-grouping': {
      flexDirection: 'column',
      gap: LABEL_GAP,
      '&.side-label': {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        '&>label': {
          ...fonts.largeLabel,
          lineHeight: '40px',
        },
      },
    },
  },

  '.united-fields': {
    display: 'flex',
    position: 'relative',
    '& > input': {
      borderBottomRightRadius: 0,
      borderTopRightRadius: 0,
      '&:hover, &:focus': {
        zIndex: 2,
      },
    },
    '& > select': {
      position: 'absolute',
      left: '52px',
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
    },
  },
  '.error-msg': {
    ...fonts.mini,
    color: colors.red,
  },

  button: {
    svg: { color: colors.blue3 },
    '&:hover': { svg: { color: colors.blue2 } },
    '&:active': {
      svg: { color: colors.blue4 },
    },
  },
};

// @include text;
//   &:focus{
//     outline:none;
//   }
// }
